import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Layout,
  Result,
  Space,
  Steps,
  Typography,
  message,
} from 'antd'
import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import {
  registerTenant,
  getRegistrationStatus,
  getRegistrationSettings,
  sendRegistrationCode,
  confirmRegistrationCode,
  type VerificationChannel,
} from '../../features/poll/api'

type VerifyState = {
  target: string
  verificationId?: string
  token?: string
  code?: string
  resendAt?: number
  sending?: boolean
  confirming?: boolean
  error?: string | null
}

const CHANNEL_LABEL: Record<VerificationChannel, string> = {
  email: 'Email',
  phone: 'Телефон (SMS)',
}

const CHANNEL_CODE_LEN: Record<VerificationChannel, number> = {
  email: 6,
  phone: 4,
}

/** Нормализация для сравнения: подтверждение привязано к конкретному адресу/номеру. */
function verifyKey(channel: VerificationChannel, value: unknown): string {
  const raw = String(value || '').trim()
  if (channel === 'email') return raw.toLowerCase()
  let digits = raw.replace(/\D/g, '')
  if (digits.length === 10) digits = `7${digits}`
  else if (digits.length === 11 && digits.startsWith('8')) digits = `7${digits.slice(1)}`
  return digits
}

function isRuPhone(value: unknown): boolean {
  return /^7\d{10}$/.test(verifyKey('phone', value))
}

const { Title, Text } = Typography

type RegistrationState = 'active' | 'submitted' | 'done' | 'failed'

const stepByStatus: Record<string, number> = {
  active: 0,
  provisioning: 1,
  done: 2,
  failed: 1,
}

/** Статус задачи провижининга → понятный текст */
const JOB_STATUS_LABELS: Record<string, string> = {
  pending: 'В очереди',
  running: 'Выполняется',
  done: 'Готово',
  failed: 'Ошибка',
  provisioning: 'Создаём кабинет',
}

/** Технический шаг → что происходит для пользователя */
const JOB_STEP_LABELS: Record<string, string> = {
  db_provision: 'Создаём базу данных для вашего кабинета',
  migrate: 'Настраиваем кабинет и загружаем данные',
  activate: 'Подключаем домен и активируем доступ',
}

function localizeJobStatus(status: string | null): string {
  if (!status) return 'Ожидаем начала'
  return JOB_STATUS_LABELS[status] || status
}

function localizeJobStep(step: string | null): string {
  if (!step) return 'Подготовка…'
  return JOB_STEP_LABELS[step] || step
}

function getErrorMessage(error: any): string {
  const first = error?.errors?.[0]
  return first?.detail || first?.message || error?.message || 'Произошла ошибка'
}

export default function TenantRegistrationPage() {
  const [form] = Form.useForm()
  const [state, setState] = useState<RegistrationState>('active')
  const [loading, setLoading] = useState(false)
  const [jobId, setJobId] = useState<string | null>(null)
  const [hostname, setHostname] = useState<string | null>(null)
  const [loginUrl, setLoginUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<string | null>(null)
  const [jobStep, setJobStep] = useState<string | null>(null)
  const [required, setRequired] = useState<VerificationChannel[]>(['email', 'phone'])
  const [verify, setVerify] = useState<Partial<Record<VerificationChannel, VerifyState>>>({})
  const [verifyOpen, setVerifyOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    getRegistrationSettings().then(s => {
      const list: VerificationChannel[] = []
      if (s.require_email) list.push('email')
      if (s.require_phone) list.push('phone')
      setRequired(list)
    })
  }, [])

  useEffect(() => {
    if (!verifyOpen) return
    const id = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(id)
  }, [verifyOpen])

  /** Токен действителен, только если адрес/номер не меняли после подтверждения. */
  const tokenFor = useCallback(
    (channel: VerificationChannel): string | undefined => {
      const st = verify[channel]
      const current = verifyKey(channel, form.getFieldValue(channel))
      return st?.token && st.target === current ? st.token : undefined
    },
    [verify, form],
  )

  const patchVerify = (channel: VerificationChannel, patch: Partial<VerifyState>) =>
    setVerify(prev => ({ ...prev, [channel]: { ...(prev[channel] as VerifyState), ...patch } }))

  const sendCode = async (channel: VerificationChannel) => {
    const value = String(form.getFieldValue(channel) || '').trim()
    const target = verifyKey(channel, value)
    setVerify(prev => ({ ...prev, [channel]: { target, sending: true, error: null } }))
    try {
      const data = await sendRegistrationCode(channel, value)
      patchVerify(channel, {
        sending: false,
        verificationId: data.verification_id,
        resendAt: Date.now() + (data.resend_in || 60) * 1000,
      })
    } catch (e) {
      patchVerify(channel, { sending: false, error: getErrorMessage(e) })
    }
  }

  const confirmCode = async (channel: VerificationChannel) => {
    const st = verify[channel]
    const codeLen = CHANNEL_CODE_LEN[channel]
    if (!st?.verificationId || !new RegExp(`^\\d{${codeLen}}$`).test(String(st.code || ''))) {
      patchVerify(channel, {
        error:
          channel === 'phone' ? 'Введите 4 цифры из SMS' : 'Введите 6 цифр из письма',
      })
      return
    }
    patchVerify(channel, { confirming: true, error: null })
    try {
      const data = await confirmRegistrationCode(st.verificationId, String(st.code))
      patchVerify(channel, { confirming: false, token: data.token })
    } catch (e) {
      patchVerify(channel, { confirming: false, error: getErrorMessage(e) })
    }
  }

  const loadProgress = useCallback(async () => {
    if (!jobId) return
    try {
      const data = await getRegistrationStatus(jobId)
      const currentJobStatus = String(data?.status || '')
      const currentJobStep = String(data?.step || '')
      setLoginUrl(hostname ? `https://${hostname}/login` : null)
      setJobStatus(currentJobStatus || null)
      setJobStep(currentJobStep || null)
      if (currentJobStatus === 'done') {
        setState('done')
      } else if (currentJobStatus === 'failed') {
        setState('failed')
        setError(
          data?.error_message ||
            'Не удалось создать кабинет. Попробуйте позже или обратитесь в поддержку.',
        )
      } else {
        setState('submitted')
      }
    } catch (e) {
      setError(getErrorMessage(e))
    }
  }, [jobId, hostname])

  useEffect(() => {
    if (state !== 'submitted') return
    loadProgress()
    const id = window.setInterval(loadProgress, 3000)
    return () => window.clearInterval(id)
  }, [loadProgress, state])

  const onFinish = useCallback(async values => {
    // Сначала подтверждаем email/телефон (обязательные — по настройке), затем создаём кабинет
    const missing = required.filter(channel => !tokenFor(channel))
    if (missing.length) {
      setVerifyOpen(true)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const payload = {
        company_name: String(values.company_name || '').trim(),
        contact_name: values.contact_name ? String(values.contact_name).trim() : null,
        phone: values.phone ? String(values.phone).trim() : null,
        email: values.email ? String(values.email).trim() : null,
        email_verification_token: tokenFor('email') || null,
        phone_verification_token: tokenFor('phone') || null,
      }
      const result = await registerTenant(payload)
      setJobId(result?.provisioning_job_id || null)
      setHostname(result?.hostname || null)
      setState('submitted')
      message.success('Заявка принята, запускаем создание кабинета')
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setLoading(false)
    }
  }, [required, tokenFor])

  const allConfirmed = required.every(channel => Boolean(tokenFor(channel)))

  const stepCurrent = state === 'done' ? 2 : stepByStatus[state] || 0

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5', padding: 20 }}>
      <Space
        direction="vertical"
        size={24}
        style={{ width: '100%', maxWidth: 720, margin: '40px auto' }}
      >
        <Card>
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Title level={2} style={{ marginBottom: 8 }}>
                Регистрация нового дилера
              </Title>
              <Text type="secondary">
                Заполните данные компании, чтобы создать отдельный кабинет. Ссылку на личный кабинет
                мы отправим на указанный email.
              </Text>
            </div>
            <Steps
              current={stepCurrent}
              items={[
                { title: 'Регистрационные данные' },
                { title: 'Создание кабинета' },
                { title: 'Готово' },
              ]}
            />
            {state === 'active' && (
              <>
                {error && <Alert type="error" showIcon message={error} />}
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  onValuesChange={() => setNow(Date.now())}
                  autoComplete="off"
                >
                  <Form.Item
                    name="company_name"
                    label="Название дилера"
                    rules={[{ required: true, message: 'Укажите название дилера' }]}
                  >
                    <Input placeholder='ООО "Ромашка"' />
                  </Form.Item>

                  <Form.Item
                    name="contact_name"
                    label="Контактное лицо"
                    rules={[{ required: true, message: 'Укажите ФИО контактного лица' }]}
                  >
                    <Input placeholder="Иван Иванов" />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="Телефон"
                    rules={[
                      { required: true, message: 'Укажите телефон' },
                      {
                        validator: (_, v) =>
                          !v || isRuPhone(v)
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error('Российский номер, например +7 900 123-45-67'),
                              ),
                      },
                    ]}
                  >
                    <Input placeholder="+7 999 123-45-67" />
                  </Form.Item>

                  <Form.Item
                    name="email"
                    label="Email"
                    rules={[
                      { type: 'email', message: 'Введите корректный email' },
                      { required: true, message: 'Укажите email' },
                    ]}
                  >
                    <Input placeholder="dealer@example.ru" />
                  </Form.Item>

                  {verifyOpen && required.length > 0 && (
                    <Card size="small" title="Подтверждение контактов" style={{ marginBottom: 16 }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {required.map(channel => {
                          const st = verify[channel]
                          const confirmed = Boolean(tokenFor(channel))
                          const sentForCurrent =
                            st?.verificationId &&
                            st.target === verifyKey(channel, form.getFieldValue(channel))
                          const wait = st?.resendAt ? Math.ceil((st.resendAt - now) / 1000) : 0
                          return (
                            <div key={channel}>
                              <Text strong>{CHANNEL_LABEL[channel]}: </Text>
                              <Text>{String(form.getFieldValue(channel) || '—')}</Text>
                              {confirmed ? (
                                <Text type="success" style={{ marginLeft: 8 }}>
                                  <CheckCircleOutlined /> подтверждён
                                </Text>
                              ) : (
                                <Space wrap style={{ display: 'flex', marginTop: 8 }}>
                                  {sentForCurrent && (
                                    <>
                                      <Input
                                        style={{ width: 140 }}
                                        maxLength={CHANNEL_CODE_LEN[channel]}
                                        inputMode="numeric"
                                        placeholder={
                                          channel === 'phone'
                                            ? 'Код из 4 цифр'
                                            : 'Код из 6 цифр'
                                        }
                                        value={st?.code}
                                        onChange={e =>
                                          patchVerify(channel, {
                                            code: e.target.value
                                              .replace(/\D/g, '')
                                              .slice(0, CHANNEL_CODE_LEN[channel]),
                                          })
                                        }
                                        onPressEnter={() => confirmCode(channel)}
                                      />
                                      <Button
                                        type="primary"
                                        loading={st?.confirming}
                                        onClick={() => confirmCode(channel)}
                                      >
                                        Подтвердить
                                      </Button>
                                    </>
                                  )}
                                  <Button
                                    loading={st?.sending}
                                    disabled={Boolean(sentForCurrent && wait > 0)}
                                    onClick={() => sendCode(channel)}
                                  >
                                    {sentForCurrent
                                      ? wait > 0
                                        ? `Отправить снова через ${wait} с`
                                        : 'Отправить снова'
                                      : 'Отправить код'}
                                  </Button>
                                </Space>
                              )}
                              {st?.error && (
                                <div>
                                  <Text type="danger">{st.error}</Text>
                                </div>
                              )}
                            </div>
                          )
                        })}
                      </Space>
                    </Card>
                  )}

                  <Button type="primary" htmlType="submit" loading={loading} block>
                    {verifyOpen && !allConfirmed ? 'Подтвердите контакты' : 'Создать кабинет'}
                  </Button>
                </Form>
              </>
            )}
            {state === 'submitted' && (
              <>
                {error && <Alert type="warning" showIcon message={error} />}
                <Result
                  icon={<LoadingOutlined />}
                  title="Создаём кабинет"
                  subTitle={
                    <Space direction="vertical" size={4}>
                      <Text>
                        {hostname ? `Домен: ${hostname}` : 'Подготавливаем домен и базу данных'}
                      </Text>
                      <Text type="secondary">Статус: {localizeJobStatus(jobStatus)}</Text>
                      <Text type="secondary">{localizeJobStep(jobStep)}</Text>
                    </Space>
                  }
                />
              </>
            )}
            {state === 'done' && (
              <Result
                status="success"
                icon={<CheckCircleOutlined />}
                title="Кабинет создан"
                subTitle={hostname ? `Домен ${hostname} готов к работе.` : undefined}
                extra={
                  loginUrl ? (
                    <Button type="primary" href={loginUrl}>
                      Перейти ко входу
                    </Button>
                  ) : null
                }
              />
            )}
            {state === 'failed' && (
              <Result
                status="error"
                title="Не удалось создать кабинет"
                subTitle={
                  error ||
                  'Не удалось завершить создание. Напишите в поддержку — мы поможем разобраться.'
                }
                extra={
                  <Button
                    onClick={() => {
                      setState('active')
                      setError(null)
                      setJobId(null)
                      setHostname(null)
                      setLoginUrl(null)
                      setJobStatus(null)
                      setJobStep(null)
                    }}
                  >
                    Попробовать снова
                  </Button>
                }
              />
            )}
          </Space>
        </Card>
      </Space>
    </Layout>
  )
}
